import {useRouteLoaderData} from '@remix-run/react'
import type {FunctionComponent, HTMLAttributes} from 'react'
import {Fragment} from 'react'
import clsx from 'clsx'
import {getTranslation} from '~/localization/translation'
import type {RootLoaderResponse, VariationDetailsProps} from '~/types'
import {getDomainConfig} from '~/domains'

export interface VariationDetails extends HTMLAttributes<HTMLDivElement> {
  index: number
  variations?: VariationDetailsProps[]
  reportVariantsClicked: () => void
}

export const cleanVariation = (variation: string): string => {
  return variation?.replace('Name', '').trim()
}

export const getPluralEnding = (word: string, isPlural: boolean): string => {
  if (!isPlural) return word

  switch (true) {
    case Boolean(word.match(/(s|x|z)$/i)):
      return `${word}es`
    case word.toLowerCase().endsWith('y'):
      return `${word.slice(0, -1)}ies`
    default:
      return `${word}s`
  }
}

const VariantsIndication: FunctionComponent<VariationDetails> = ({
  index,
  variations,
  reportVariantsClicked,
  ...attributes
}) => {
  const {language, deviceType, locale, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const variantsToDisplay = variations
    ?.slice(0, deviceType !== 'desktop' ? 2 : 3)
    .filter((v) => v?.numberOfVariations > 1)
  return (
    Boolean(variantsToDisplay?.length) && (
      <div {...attributes} onClick={reportVariantsClicked} role="button" tabIndex={0} onKeyDown={() => {}}>
        <p className="cursor-auto text-base font-medium">{translations.ProductCard.variantsIndication.title}</p>
        {variantsToDisplay?.map((variation, i) => {
          const isPlural = variation.numberOfVariations === 0 || variation.numberOfVariations > 1
          const isLast = i === variantsToDisplay.length - 1
          return variation?.numberOfVariations > 1 ? (
            <Fragment key={`${variation.dimension}-${i}`}>
              <p
                className={clsx(
                  'text-base capitalize underline underline-offset-[3px]',
                  stylesBydomain.LISTPAGE_ANCHOR_LINK,
                  'listpage_anchor_link',
                )}
              >
                {variation.numberOfVariations}&nbsp;
                {getPluralEnding(cleanVariation(variation.dimension), isPlural)}
              </p>
              {!isLast && <p className="text-base">,</p>}
            </Fragment>
          ) : null
        })}
      </div>
    )
  )
}

export default VariantsIndication
